import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, PageContainer, Table, Tabs } from '@react-apps/design-system';
import type { TabItem } from '@react-apps/design-system';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import { useRouteTabs } from '@/shared/hooks/use-route-tabs';
import { usePageQueryParam } from '@/shared/hooks/use-page-query-param';
import type { Provider } from '../../domain/entities/sales.entity';
import { useOffer } from '../hooks/use-offer';
import { useTransactions } from '../hooks/use-transactions';
import { OfferCard } from '../components/OfferCard';
import { buildSalesTransactionColumns } from '../shared/transaction-columns';
import { TablePagination } from '../shared/TablePagination';
import {
  ErrorState,
  ErrorText,
  SectionCard,
  SkeletonCard,
  getLoadErrorMessage,
  isNotFoundError,
} from '../shared/display';
import { useProductByExternalKey } from '@/modules/catalog/presentation/hooks/use-product-by-external-key';
import { useProductPerks } from '@/modules/catalog/presentation/hooks/use-product-perks';
import { useAllPerkGroups } from '@/modules/catalog/presentation/hooks/use-perks';
import {
  GRANT_POLICY_LABELS,
  PRODUCT_STATUS_BADGE,
  PRODUCT_STATUS_LABELS,
} from '@/modules/catalog/presentation/labels';
import type { ProductExternalKeyInput } from '@/modules/catalog/domain/entities/catalog.entity';
import { useTotvsProductLinkMatch } from '@/modules/totvs/presentation/hooks/use-totvs-product-link-match';
import {
  Mono as TotvsMono,
  ProductLinkActionBadge,
  TotvsProviderBadge,
} from '@/modules/totvs/presentation/display';
import type { TotvsProductLinkMatchInput } from '@/modules/totvs/domain/entities/totvs.entity';

const OFFER_TAB_IDS = ['details', 'transactions'];
const PER_PAGE = 10;

/**
 * Só Guru/Hotmart existem no vocabulário do Catalog e do TOTVS — os demais
 * gateways de Sales (Eduzz, Apple, Google) não têm entregável nem de-para
 * configurável, então a chave nem chega a ser montada para eles.
 */
function toGatewayProvider(
  provider: Provider | null,
): 'guru' | 'hotmart' | null {
  return provider === 'guru' || provider === 'hotmart' ? provider : null;
}

/** As duas abas falham igual: "não encontrada" só com 404 de fato. */
function OfferLoadError({ error }: { error: unknown }) {
  return (
    <ErrorState
      message={
        isNotFoundError(error)
          ? 'Oferta não encontrada.'
          : getLoadErrorMessage(error, 'Não foi possível carregar a oferta.')
      }
    />
  );
}

/**
 * Página `/offers/:id`, alcançada por link (o título/id da oferta no
 * `OfferCard`, ou `EntityLink kind="offer"`) — não entra no menu, mesmo
 * precedente de `/billing-agreements/:id`.
 *
 * Cruza três módulos (sales, catalog, totvs) só no front: um endpoint
 * agregador no back não é possível (`globals/` não conhece `integrations/`),
 * então cada bloco busca a própria fonte e degrada sozinho — erro/403 de um
 * módulo não derruba os outros dois nem a página.
 */
export function OfferDetailPage() {
  const { offerId } = useParams<{ offerId: string }>();
  const goBack = useBackNavigate('/sales');
  const id = offerId ?? '';
  const { activeTab, onTabChange } = useRouteTabs(
    `/offers/${id}`,
    OFFER_TAB_IDS,
  );

  const tabs: TabItem[] = [
    {
      id: 'details',
      label: 'Detalhes',
      content: <DetailsTab offerId={id} />,
    },
    {
      id: 'transactions',
      label: 'Transações',
      content: <TransactionsTab offerId={id} />,
    },
  ];

  return (
    <PageContainer maxWidth="4xl">
      <button
        onClick={goBack}
        className="text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center gap-1.5 text-sm transition-colors"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15.75 19.5 8.25 12l7.5-7.5"
          />
        </svg>
        Vendas
      </button>

      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={onTabChange} />
    </PageContainer>
  );
}

// --- Aba: Detalhes ---

function DetailsTab({ offerId }: { offerId: string }) {
  const { data: offer, isLoading, isError, error } = useOffer(offerId);

  if (isLoading) return <SkeletonCard rows={6} />;
  if (isError || !offer) return <OfferLoadError error={error} />;

  const gatewayProvider = toGatewayProvider(offer.provider);
  const externalKey: ProductExternalKeyInput | null =
    gatewayProvider && offer.externalProductId
      ? {
          provider: gatewayProvider,
          providerAccountId: offer.providerAccountId,
          productExternalId: offer.externalProductId,
          offerExternalId: offer.externalId,
        }
      : null;
  const matchInput: TotvsProductLinkMatchInput | null = externalKey
    ? { ...externalKey }
    : null;

  return (
    <div className="space-y-6">
      <OfferCard offer={offer} />
      <CatalogBlock offerName={offer.name} externalKey={externalKey} />
      <TotvsBlock matchInput={matchInput} />
    </div>
  );
}

function CatalogBlock({
  offerName,
  externalKey,
}: {
  offerName: string;
  externalKey: ProductExternalKeyInput | null;
}) {
  const navigate = useNavigate();
  const {
    data: product,
    isLoading,
    isError,
    error,
  } = useProductByExternalKey(externalKey);
  const {
    data: perks,
    isLoading: perksLoading,
    isError: perksIsError,
    error: perksError,
  } = useProductPerks(product?.id ?? '');
  const { data: perkGroups } = useAllPerkGroups();

  // O perkId identifica o par (capacidade, forma de concessão) — só o
  // catálogo agrupado sabe traduzir de volta pra key + kind (mesmo cálculo de
  // `ProductDetailPage`).
  const perkLabelById = useMemo(() => {
    const map = new Map<string, { key: string; kind: string }>();
    for (const group of perkGroups ?? []) {
      for (const form of group.grantForms) {
        map.set(form.perkId, {
          key: group.key,
          kind: GRANT_POLICY_LABELS[form.grantPolicyKind],
        });
      }
    }
    return map;
  }, [perkGroups]);

  // `TOTVS_ONLY_OFFER_MARKER`: oferta `[TV]` desliga a entrega de perks mesmo
  // com produto configurado — o bloco mentiria se não dissesse isso.
  const isTvOnly = offerName.includes('[TV]');

  return (
    <SectionCard title="Entregável no Catalog">
      {!externalKey ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Chave externa incompleta — este gateway não tem entregável
          configurável, ou a conta/produto ainda não foram resolvidos.
        </p>
      ) : isLoading ? (
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      ) : isError ? (
        <ErrorText>
          {getLoadErrorMessage(
            error,
            'Não foi possível consultar o entregável.',
          )}
        </ErrorText>
      ) : !product ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Nenhum produto cadastrado para esta chave — a entrega desta oferta
          fica <strong>SKIPPED</strong> esperando cadastro.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() =>
                void navigate(`/management/catalog/products/${product.id}`, {
                  state: { product },
                })
              }
              className="text-(--color-accent) text-sm hover:underline"
            >
              {product.name}
            </button>
            <Badge variant={PRODUCT_STATUS_BADGE[product.status]}>
              {PRODUCT_STATUS_LABELS[product.status]}
            </Badge>
          </div>

          {isTvOnly && (
            <p className="text-(--color-text-secondary) text-xs">
              O nome desta oferta traz o marcador <strong>[TV]</strong>: a
              entrega de perks é desligada mesmo com produto configurado — só a
              matrícula no TOTVS acontece nesta venda.
            </p>
          )}

          <p className="text-(--color-text-secondary) text-xs italic">
            Hoje <strong>DELIVERY_ENQUEUE_ENABLED</strong> está desligado nos
            três profiles — nenhuma entrega de perk é de fato enfileirada, em
            ambiente nenhum. A lista abaixo descreve o que está configurado, não
            o que está acontecendo.
          </p>

          {perksLoading ? (
            <p className="text-(--color-text-secondary) text-sm">
              Carregando perks…
            </p>
          ) : perksIsError ? (
            // Sem este ramo, a falha caía no vazio abaixo e a tela dizia que a
            // oferta não entrega nada — justo a pergunta que ela responde.
            <ErrorText>
              {getLoadErrorMessage(
                perksError,
                'Não foi possível carregar os perks do produto.',
              )}
            </ErrorText>
          ) : !perks || perks.length === 0 ? (
            <p className="text-(--color-text-secondary) text-sm italic">
              Nenhum perk associado a este produto.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {perks.map((pp) => (
                <li key={pp.id} className="text-sm">
                  <span className="font-mono">
                    {perkLabelById.get(pp.perkId)?.key ?? pp.perkId}
                  </span>
                  {perkLabelById.has(pp.perkId) && (
                    <span className="text-(--color-text-secondary)">
                      {' · '}
                      {perkLabelById.get(pp.perkId)?.kind}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function TotvsBlock({
  matchInput,
}: {
  matchInput: TotvsProductLinkMatchInput | null;
}) {
  const {
    data: link,
    isLoading,
    isError,
    error,
  } = useTotvsProductLinkMatch(matchInput);

  return (
    <SectionCard
      title="Matrícula no TOTVS"
      action={
        <Link
          to="/totvs/product-links"
          className="text-(--color-accent) text-xs hover:underline"
        >
          Ver de-paras
        </Link>
      }
    >
      {!matchInput ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Chave externa incompleta — este gateway não tem de-para configurável,
          ou a conta/produto ainda não foram resolvidos.
        </p>
      ) : isLoading ? (
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      ) : isError ? (
        <ErrorText>
          {getLoadErrorMessage(error, 'Não foi possível consultar o de-para.')}
        </ErrorText>
      ) : !link ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Sem de-para cadastrado — um contrato <strong>[TV]</strong> desta
          oferta falharia por erro de configuração.
        </p>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <TotvsProviderBadge provider={link.provider} />
            <ProductLinkActionBadge action={link.action} />
          </div>
          {link.action === 'ignore' ? (
            <p className="text-(--color-text-secondary) text-sm">
              {link.ignoreReason ?? '—'}
            </p>
          ) : (
            <div className="text-sm">
              <p className="text-(--color-text-primary)">
                IDAREAINTERESSE: <TotvsMono>{link.idAreaInteresse}</TotvsMono>
              </p>
              <p className="text-(--color-text-secondary) text-xs">
                Coligada {link.codColigada} · Filial {link.codFilial} · IDPS{' '}
                {link.idPs}
              </p>
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

// --- Aba: Transações ---

function TransactionsTab({ offerId }: { offerId: string }) {
  const navigate = useNavigate();
  // Na URL, não em `useState`: a linha leva a `/sales/:id`, e o "Voltar"
  // tem de reabrir a aba na página em que o operador estava.
  const [page, setPage] = usePageQueryParam();
  // Mesma checagem da aba "Detalhes" (cacheada pelo react-query, mesma
  // queryKey): sem ela, um `offerId` inexistente mostraria "Nenhuma
  // transação encontrada" — um vazio que mente, porque não é que não há
  // transações, é que a oferta não existe.
  const {
    data: offer,
    isLoading: offerLoading,
    isError: offerError,
    error: offerLoadError,
  } = useOffer(offerId);
  // Só busca depois que a oferta resolver — evita uma requisição perdida
  // quando o `offerId` da URL não existe (a tela vai mostrar "Oferta não
  // encontrada" de qualquer jeito).
  const offerResolved = !offerLoading && !offerError && !!offer;
  const { data, isLoading, isError, error } = useTransactions(
    page,
    PER_PAGE,
    { offerId },
    { enabled: offerResolved },
  );
  const columns = useMemo(() => buildSalesTransactionColumns(), []);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;

  // Só decide que a oferta falhou depois que o carregamento termina — enquanto
  // ainda carrega, a moldura (`SectionCard` + `Table`) fica de pé e quem
  // mostra o carregando é o próprio skeleton da tabela, sem trocar de forma
  // no meio do caminho.
  if (!offerLoading && (offerError || !offer)) {
    return <OfferLoadError error={offerLoadError} />;
  }

  if (isError) {
    return (
      <ErrorState
        message={getLoadErrorMessage(
          error,
          'Não foi possível carregar as transações desta oferta.',
        )}
      />
    );
  }

  return (
    <SectionCard title="Transações desta oferta">
      <Table
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(row) => row.id}
        loading={offerLoading || isLoading}
        emptyMessage="Nenhuma transação encontrada para esta oferta."
        striped
        onRowClick={(row) => void navigate(`/sales/${row.id}`)}
      />

      {totalPages > 1 && (
        <div className="flex justify-end">
          <TablePagination
            page={page}
            totalPages={totalPages}
            disabled={isLoading}
            onPageChange={setPage}
          />
        </div>
      )}
    </SectionCard>
  );
}
